import { useFirebaseAuth } from '../../contexts/FirebaseAuthProvider';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { useNavigate, useLocation } from 'react-router-dom';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaCog, FaEdit, FaEye, FaRobot, FaVideo, FaCloudUploadAlt, FaUser, FaCrown, FaCreditCard, FaChevronRight, FaSignOutAlt, FaShieldAlt, FaQuestionCircle, FaBook, FaFileContract, FaInfoCircle } from 'react-icons/fa';
import * as pdfjsLib from 'pdfjs-dist';
import { pdf } from '@react-pdf/renderer';
import { useSubscription } from '../../contexts/SubscriptionContext';
import axios from 'axios';
import MobileEditPanel from './MobileEditPanel';
import MobileResume from './MobileResume';
import MobileResumePDF from './MobileResumePDF';
import MobileWholeResumeService from './MobileWholeResumeService';
import MobileChatbotPopup from './MobileChatbotPopup';
import characterLimits from './MobileCharacterLimits';
import MobilePopup from './MobilePopup';
import MobileEditBubble from './MobileEditBubble';
import Spinner from './Spinner';
import MobileResumeStorageService from './MobileResumeStorageService';
import CreditGateModal from '../../desktop/components/CreditGateModal';
import ResumeUpdateModal from '../../desktop/components/ResumeUpdateModal';

import '../styles/mobile-base.css';
import '../styles/mobile-layout.css';
import '../styles/mobile-components.css';
import '../styles/mobile-popup.css';
import '../styles/mobile-chatbot.css';
import '../styles/mobile-print.css';
import '../styles/mobile-editbubble.css';
import '../styles/mobile-settings.css';
import '../styles/mobile-editpanel.css';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const DEFAULT_RESUME_DATA = {
  name: 'Guest',
  title: 'Senior Software Engineer',
  location: 'San Francisco, CA',
  email: 'john.doe@example.com',
  phone: '+1 (555) 019-2834',
  summary: 'Innovative and detail-oriented Software Engineer with 6+ years of experience designing, building, and deploying scalable web applications. Proficient in modern JavaScript frameworks, cloud infrastructure, and agile methodologies.',
  experience: 'Senior Software Engineer | TechSolutions Inc. | 2022 - Present\n- Led a team of 4 developers to redesign and migrate a legacy monolithic platform to a modern microservices architecture, improving system uptime to 99.9%.\n- Integrated scalable REST and GraphQL APIs using Node.js and TypeScript, reducing client page load latency by 35%.\n- Implemented automated CI/CD deployment pipelines on AWS, decreasing software release cycle time by 40%.\n\nSoftware Developer | InnovateWeb Co. | 2019 - 2022\n- Developed and maintained responsive web applications using React and Redux, enhancing user engagement metrics by 20%.\n- Optimized database query performance in PostgreSQL, resulting in a 50% speedup for core dashboard search features.',
  skills: 'JavaScript / TypeScript\nReact / Next.js\nNode.js / Express\nSQL / PostgreSQL\nAmazon Web Services (AWS)\nGit / CI/CD Pipelines',
  education: 'Bachelor of Science in Computer Science\nState University, 2015 - 2019',
  publications: '- Author, "Building Scalable Microservices with Node.js" — TechBlog Publications, March 2024\n- Core Contributor, Open-source React UI Framework "SleekUI" (GitHub, 2K+ Stars)',
  jobDescription: '',
  companyName: '',
  coverLetter: '',
  applicantAddress: '',
  recipientName: '',
  recipientJobTitle: '',
  coverLetterBody: ''
};

const MobileApp = () => {
  const { user, signOut, getToken } = useFirebaseAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { tier, status, credits, fetchSubscription } = useSubscription();
  const isGated = tier === 'free' || !tier || status === 'trialing';
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
  const [creditModalType, setCreditModalType] = useState('pdf_confirm');
  const [onCreditModalConfirm, setOnCreditModalConfirm] = useState(null);
  const [proposedUpdate, setProposedUpdate] = useState(null); // { sectionKey, sectionName, currentContent, proposedContent }

  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [isSqueezed, setIsSqueezed] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_RESUME_DATA);

  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusTimeout, setStatusTimeout] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const { darkMode, toggleDarkMode: toggleGlobalDarkMode } = useDarkMode();
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  // cover letter toggle state reserved for future
  const [isZoomed, setIsZoomed] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  // scroll-to-bottom indicator reserved for future
  const [loadingButtons, setLoadingButtons] = useState({});
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgressMessage, setGenerationProgressMessage] = useState('');

  const resumeRef = useRef(null);
  const zoomContainerRef = useRef(null);

  // Bottom Navigation and Touch Zoom State variables
  const [activeTab, setActiveTab] = useState('preview');
  const [scale, setScale] = useState(1);
  const [touchStartPos, setTouchStartPos] = useState({ x: 0, y: 0 });
  const [touchStartScroll, setTouchStartScroll] = useState({ left: 0, top: 0 });
  const [pinchStartDist, setPinchStartDist] = useState(0);

  // Sync tab active state with panel visibility states
  useEffect(() => {
    if (isEditPanelOpen) {
      setActiveTab('editor');
    } else if (isChatbotOpen) {
      setActiveTab('coach');
    } else {
      setActiveTab('preview');
    }
  }, [isEditPanelOpen, isChatbotOpen]);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    if (tab === 'editor') {
      setIsEditPanelOpen(true);
      setIsChatbotOpen(false);
    } else if (tab === 'coach') {
      setIsEditPanelOpen(false);
      setIsChatbotOpen(true);
    } else if (tab === 'settings') {
      setIsEditPanelOpen(false);
      setIsChatbotOpen(false);
    } else if (tab === 'interview') {
      setIsEditPanelOpen(false);
      setIsChatbotOpen(false);
      navigate('/interview-simulator', { state: { activeTab: 'interview' } });
    } else {
      setIsEditPanelOpen(false);
      setIsChatbotOpen(false);
    }
  }, [navigate]);

  // Sync incoming route states
  useEffect(() => {
    if (location.state?.activeTab) {
      handleTabChange(location.state.activeTab);
    }
  }, [location, handleTabChange]);

  // Lock body scroll on active tab changes to prevent viewport rubber-banding/bouncing
  useEffect(() => {
    if (activeTab === 'editor' || activeTab === 'coach') {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, [activeTab]);

  useEffect(() => {
    if (!user) {
      setFormData(DEFAULT_RESUME_DATA);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      const pendingStr = sessionStorage.getItem('coach_pending');
      if (pendingStr) {
        try {
          const pending = JSON.parse(pendingStr);
          const age = Date.now() - pending.timestamp;
          if (age < 600000 && pending.returnTab === 'coach') {
            handleTabChange('coach');
          }
        } catch (e) {
          console.error("Error checking pending coach tab redirect:", e);
        }
      }
    }
  }, [user, handleTabChange]);

  const toggleDarkMode = () => {
    toggleGlobalDarkMode();
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 700);
  };

  const toggleChatbot = () => {
    if (!user) {
      navigate('/sign-in');
      return;
    }
    if (isGated && (credits?.remainingCredits || 0) <= 0) {
      setCreditModalType('ai_insufficient');
      setIsCreditModalOpen(true);
      return;
    }
    setIsChatbotOpen(!isChatbotOpen);
  };

  const handleShowCreditGate = useCallback((type) => {
    setCreditModalType(type);
    setIsCreditModalOpen(true);
  }, []);



  // kept for future use

  const toggleEditPanel = () => {
    if (isEditPanelOpen) {
      const panel = document.querySelector('.mobile-edit-panel');
      panel.classList.add('closing');

      setTimeout(() => {
        setIsEditPanelOpen(false);
        panel.classList.remove('closing');
      }, 500);
    } else {
      setIsEditPanelOpen(true);
    }
  };

  const handleClosePanel = () => {
    setIsSqueezed(true);
    setTimeout(() => {
      setIsEditPanelOpen(false);
      setIsSqueezed(false);
    }, 600);
  };

  const changeTemplate = (template) => {
    console.log(`Changing to template: ${template}`);
    if (isGated && (template === 'modern' || template === 'creative')) {
      setCreditModalType('template_lock');
      setIsCreditModalOpen(true);
      setTimeout(() => {
        const select = document.getElementById('templateSelect');
        if (select) select.value = 'standard';
      }, 0);
    }
  };

  const handleCoverLetterGenerate = async () => {
    if (!user) {
      navigate('/sign-in');
      return;
    }
    if (isGated && (credits?.remainingCredits || 0) <= 0) {
      setCreditModalType('ai_insufficient');
      setIsCreditModalOpen(true);
      return;
    }
    try {
      clearStatusMessage();
      setIsLoading(true);
      setIsGenerating(true);
      setGenerationProgressMessage('Analyzing job profile for cover letter...');

      const updateStatus = (message) => {
        setGenerationProgressMessage(message);
      };

      const generatedCoverLetter = await MobileWholeResumeService.generateCoverLetter(formData, updateStatus);

      setFormData(prevData => ({
        ...prevData,
        coverLetter: generatedCoverLetter,
        jobDescription: formData.jobDescription,
        companyName: formData.companyName,
        recipientName: formData.recipientName,
        recipientJobTitle: formData.recipientJobTitle,
        coverLetterBody: generatedCoverLetter
      }));

      setStatusMessage({ text: 'Cover letter generation complete.', duration: 5000, status: 'success' });
    } catch (error) {
      console.error('Error generating cover letter:', error);
      setStatusMessage({ text: 'Error generating cover letter. Please try again.', duration: 5000, status: 'error' });
    } finally {
      setIsLoading(false);
      setIsGenerating(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (characterLimits[name] === undefined || value.length <= characterLimits[name]) {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    }
  };

  const handleWholeResumeGenerate = async () => {
    if (!user) {
      navigate('/sign-in');
      return;
    }
    if (isGated && (credits?.remainingCredits || 0) <= 0) {
      setCreditModalType('ai_insufficient');
      setIsCreditModalOpen(true);
      return;
    }
    try {
      clearStatusMessage();
      setLoadingButtons(prev => ({ ...prev, wholeResume: true }));
      setIsGenerating(true);
      setGenerationProgressMessage('Connecting to AI writing service...');

      const updateStatus = (message) => {
        setGenerationProgressMessage(message);
      };

      const generatedData = await MobileWholeResumeService.generateWholeResume(formData, characterLimits, updateStatus);
      setFormData((prevData) => ({
        ...prevData,
        ...generatedData,
      }));

      setStatusMessage({ text: 'Whole resume generation complete.', duration: 5000, status: 'success' });
    } catch (error) {
      console.error('Error generating whole resume with Gemini:', error);
      setStatusMessage({ text: 'Error generating whole resume. Please try again.', duration: 5000, status: 'error' });
    } finally {
      setLoadingButtons(prev => ({ ...prev, wholeResume: false }));
      setIsGenerating(false);
    }
  };

  const handleSectionGenerate = async (section) => {
    if (!user) {
      navigate('/sign-in');
      return;
    }
    if (isGated && (credits?.remainingCredits || 0) <= 0) {
      setCreditModalType('ai_insufficient');
      setIsCreditModalOpen(true);
      return;
    }
    try {
      clearStatusMessage();
      setLoadingButtons(prev => ({ ...prev, [section]: true }));
      setIsGenerating(true);
      setGenerationProgressMessage(`Generating ${section} with Gemini...`);

      const updatedData = await MobileWholeResumeService.generateSection(section, formData, characterLimits);
      setFormData((prevData) => ({
        ...prevData,
        [section]: updatedData[section],
      }));

      setStatusMessage({ text: `${section} Generation complete.`, duration: 5000, status: 'success' });
    } catch (error) {
      console.error(`Error generating ${section} with Gemini:`, error);
      setStatusMessage({ text: `Error generating ${section}. ${error.message}`, duration: 5000, status: 'error' });
    } finally {
      setLoadingButtons(prev => ({ ...prev, [section]: false }));
      setIsGenerating(false);
    }
  };

  const generatePDF = async () => {
    if (!user) {
      navigate('/sign-in');
      return;
    }

    const downloadAction = async () => {
      const blob = await pdf(<MobileResumePDF {...formData} />).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');

      const link = document.createElement('a');
      link.href = url;
      link.download = 'resume.pdf';
      link.click();

      setTimeout(() => URL.revokeObjectURL(url), 100);
      if (fetchSubscription) {
        fetchSubscription();
      }
    };

    if (isGated) {
      const remaining = credits?.remainingCredits || 0;
      if (remaining >= 100) {
        setCreditModalType('pdf_confirm');
        setOnCreditModalConfirm(() => async () => {
          setIsCreditModalOpen(false);
          try {
            setLoadingButtons(prev => ({ ...prev, generatePDF: true }));
            setStatusMessage({ text: 'Charging credits...', duration: 5000, status: 'info' });
            const token = await getToken();
            const res = await axios.post('/api/resume/charge-pdf', {}, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.data?.success) {
              if (fetchSubscription) {
                fetchSubscription();
              }
              setStatusMessage({ text: 'Generating PDF...', duration: 5000, status: 'info' });
              await downloadAction();
              setStatusMessage({ text: 'PDF generation complete.', duration: 5000, status: 'success' });
            } else {
              throw new Error('Deduction failed');
            }
          } catch (error) {
            console.error('PDF credit charge failed:', error);
            alert(error.response?.data?.error || 'Failed to deduct credits. Please try again.');
            setStatusMessage({ text: 'Deduction failed.', duration: 3000, status: 'error' });
          } finally {
            setLoadingButtons(prev => ({ ...prev, generatePDF: false }));
          }
        });
        setIsCreditModalOpen(true);
      } else {
        setCreditModalType('pdf_insufficient');
        setIsCreditModalOpen(true);
      }
      return;
    }

    // Normal subscriber flow
    try {
      setLoadingButtons(prev => ({ ...prev, generatePDF: true }));
      setStatusMessage({ text: 'Generating PDF...', duration: 5000, status: 'info' });
      await downloadAction();
      setStatusMessage({ text: 'PDF generation complete.', duration: 5000, status: 'success' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      setStatusMessage({ text: 'Error generating PDF. Please try again.', duration: 5000, status: 'error' });
    } finally {
      setLoadingButtons(prev => ({ ...prev, generatePDF: false }));
    }
  };

  const clearStatusMessage = () => {
    try {
      if (statusTimeout) {
        clearTimeout(statusTimeout);
        setStatusTimeout(null);
      }
      setStatusMessage('');
    } catch (error) {
      console.error("Error clearing status message:", error);
    }
  };

  const toggleSection = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      // Single touch: setup drag panning
      const touch = e.touches[0];
      setTouchStartPos({ x: touch.clientX, y: touch.clientY });
      setTouchStartScroll({
        left: zoomContainerRef.current.scrollLeft,
        top: zoomContainerRef.current.scrollTop
      });
    } else if (e.touches.length === 2) {
      // Multi-touch: setup pinch zoom
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      setPinchStartDist(dist);
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 1 && scale > 1) {
      // Pan the viewport
      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartPos.x;
      const deltaY = touch.clientY - touchStartPos.y;
      zoomContainerRef.current.scrollLeft = touchStartScroll.left - deltaX * 1.5;
      zoomContainerRef.current.scrollTop = touchStartScroll.top - deltaY * 1.5;
    } else if (e.touches.length === 2) {
      // Zoom the viewport
      e.preventDefault();
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      if (pinchStartDist > 0) {
        const newScale = Math.min(3, Math.max(1, scale * (dist / pinchStartDist)));
        setScale(newScale);
        resumeRef.current.style.transform = `scale(${newScale})`;
      }
    }
  };

  const handleTouchEnd = () => {
    setPinchStartDist(0);
    // Snap back to 1x zoom if we get close to it
    if (scale < 1.1) {
      setScale(1);
      resumeRef.current.style.transform = 'scale(1)';
      resumeRef.current.style.transformOrigin = 'center center';
      setIsZoomed(false);
    } else {
      setIsZoomed(true);
    }
  };

  const handleTapZoom = (e) => {
    // Zoom in at click position or zoom out if already zoomed
    if (scale === 1) {
      const resume = resumeRef.current;
      const rect = resume.getBoundingClientRect();
      const clientX = e.clientX || (e.touches && e.touches[0]?.clientX) || e.pageX || 0;
      const clientY = e.clientY || (e.touches && e.touches[0]?.clientY) || e.pageY || 0;
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      resume.style.transformOrigin = `${x}px ${y}px`;
      resume.style.transform = 'scale(1.8)';
      setScale(1.8);
      setIsZoomed(true);
    } else {
      resumeRef.current.style.transform = 'scale(1)';
      resumeRef.current.style.transformOrigin = 'center center';
      setScale(1);
      setIsZoomed(false);
    }
  };

  // Keep handleZoom for backwards compatibility or click fallback
  const handleZoom = (e) => {
    handleTapZoom(e);
  };

  const unzoom = () => {
    resumeRef.current.style.transform = 'scale(1)';
    resumeRef.current.style.transformOrigin = 'center center';
    setScale(1);
    setIsZoomed(false);
  };

  const handleMouseMove = (e) => {
    if (isZoomed && scale > 1) {
      const container = zoomContainerRef.current;
      const speed = 2;
      container.scrollLeft -= e.movementX * speed;
      container.scrollTop -= e.movementY * speed;
    }
  };

  const handleMouseLeave = () => {
    // Do not auto-reset zoom on mobile leave to support touch panning
  };

  // reserved for future scroll indicator

  // reserved for future smooth scroll

  // easing util reserved for future

  const saveResume = useCallback(async () => {
    if (!user) {
      navigate('/sign-in');
      return;
    }
    try {
      setIsLoading(true);
      const token = await getToken();
      await MobileResumeStorageService.saveResume(formData, token);
      setStatusMessage({ text: 'Resume saved successfully', duration: 3000, status: 'success' });
    } catch (error) {
      setStatusMessage({ text: 'Error saving resume', duration: 3000, status: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [formData, getToken]);

  const loadResume = useCallback(async () => {
    if (!user) {
      navigate('/sign-in');
      return;
    }
    try {
      setIsLoading(true);
      const token = await getToken();
      const loadedData = await MobileResumeStorageService.loadResume(token);
      if (loadedData) {
        setFormData(prevData => {
          const keepIfNonEmpty = (localVal, serverVal) => {
            const s = (serverVal ?? '');
            const l = (localVal ?? '');
            return s && String(s).trim().length > 0 ? s : l;
          };
          return {
            ...prevData,
            name: keepIfNonEmpty(prevData.name, loadedData.name),
            title: keepIfNonEmpty(prevData.title, loadedData.title),
            location: keepIfNonEmpty(prevData.location, loadedData.location),
            email: keepIfNonEmpty(prevData.email, loadedData.email),
            phone: keepIfNonEmpty(prevData.phone, loadedData.phone),
            summary: keepIfNonEmpty(prevData.summary, loadedData.summary),
            experience: keepIfNonEmpty(prevData.experience, loadedData.experience),
            skills: keepIfNonEmpty(prevData.skills, loadedData.skills),
            education: keepIfNonEmpty(prevData.education, loadedData.education),
            publications: keepIfNonEmpty(prevData.publications, loadedData.publications),
            coverLetter: keepIfNonEmpty(prevData.coverLetter, loadedData.coverLetter),
            applicantAddress: keepIfNonEmpty(prevData.applicantAddress, loadedData.applicantAddress),
            recipientName: keepIfNonEmpty(prevData.recipientName, loadedData.recipientName),
            recipientJobTitle: keepIfNonEmpty(prevData.recipientJobTitle, loadedData.recipientJobTitle),
            coverLetterBody: keepIfNonEmpty(prevData.coverLetterBody, loadedData.coverLetterBody),
            companyName: keepIfNonEmpty(prevData.companyName, loadedData.companyName),
            jobDescription: keepIfNonEmpty(prevData.jobDescription, loadedData.jobDescription)
          };
        });
        setStatusMessage({ text: 'Resume loaded successfully', duration: 3000, status: 'success' });
      }
    } catch (error) {
      setStatusMessage({ text: 'Error loading resume', duration: 3000, status: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [getToken]);

  // Add auto-save functionality
  useEffect(() => {
    const debounced = debounce(() => { saveResume(); }, 5000);
    if (user) debounced();
    return () => debounced.cancel();
  }, [formData, user, saveResume]);

  // Debounce function
  const debounce = (func, delay) => {
    let timeoutId;
    const debouncedFunc = (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
    debouncedFunc.cancel = () => clearTimeout(timeoutId);
    return debouncedFunc;
  };

  // Load resume on initial load
  useEffect(() => {
    if (user) {
      loadResume();
    }
  }, [user, loadResume]);

  const extractTextFromPdf = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdfDoc = await loadingTask.promise;
    let fullText = '';
    for (let i = 1; i <= pdfDoc.numPages; i++) {
      const page = await pdfDoc.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(' ');
      fullText += pageText + '\n';
    }
    return fullText;
  };

  const handleImportClick = () => {
    if (!user) {
      alert("Please sign in or register to use the AI Document Upload feature.");
      navigate('/sign-in');
      return;
    }
    const remaining = credits?.remainingCredits || 0;
    if (remaining < 5) {
      handleShowCreditGate('ai_insufficient');
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    try {
      let text = '';
      if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
        text = await file.text();
      } else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        text = await extractTextFromPdf(file);
      } else {
        alert('Unsupported file type. Please upload a PDF or TXT file.');
        setIsImporting(false);
        return;
      }

      if (!text.trim()) {
        alert('Could not extract any text from the document. Please ensure it is not scanned or empty.');
        setIsImporting(false);
        return;
      }

      const token = await getToken();
      const response = await fetch('/api/ai/gemini/parse-resume', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ resumeText: text })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to parse resume');
      }

      const data = await response.json();
      if (data.success && data.parsedData) {
        const parsed = data.parsedData;
        setFormData(prevData => {
          const newData = { ...prevData };
          Object.keys(parsed).forEach(key => {
            if (parsed[key] !== undefined && parsed[key] !== null) {
              newData[key] = parsed[key];
            }
          });
          return newData;
        });
        alert('Resume imported and parsed successfully!');
        if (fetchSubscription) {
          fetchSubscription();
        }
      } else {
        throw new Error('Invalid parsing response');
      }
    } catch (err) {
      console.error(err);
      alert('Error parsing resume: ' + err.message);
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className={`mobile-app ${darkMode ? 'dark-mode' : ''}`}>
      <div>
          <header className="mobile-header">
            {isGated && (
              <button 
                className="upgrade-header-btn" 
                onClick={() => navigate('/pricing')}
                aria-label="Upgrade to premium plan"
              >
                <FaCrown style={{ marginRight: '4px' }} />
                <span>Go Pro</span>
              </button>
            )}
            <div className="logo-banner" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
              <img
                src={darkMode ? "/logo.png" : "/logo.png"}
                alt="Resumai Logo Banner"
                className={`${darkMode ? "dark-mode-logo" : "light-mode-logo"} ${isAnimating ? "mode-switch" : ""}`}
              />
            </div>
            <div className="user-info-badge">
              Hey, {user?.displayName?.trim().split(/\s+/)[0] || user?.email?.split('@')[0]?.split('.')[0] || formData?.name?.trim().split(/\s+/)[0] || 'Guest'}
            </div>
            {statusMessage && (
              <MobilePopup
                message={statusMessage.text}
                onClose={() => setStatusMessage('')}
                duration={statusMessage.duration || 3000}
                type={statusMessage.type || 'top'}
                status={statusMessage.status || 'success'}
              />
            )}
          </header>

          {/* Settings Tab View (Full Page) */}
          {activeTab === 'settings' && (
            <div className="mobile-settings-tab">
              <h2 className="settings-page-title">Settings</h2>
              
              {/* Profile Card */}
              <div className="settings-profile-card">
                <div className="profile-avatar">
                  <FaUser />
                </div>
                <div className="profile-info">
                  <h4 className="profile-name">
                    {user?.displayName || formData?.name || 'Guest User'}
                  </h4>
                  <p className="profile-email">
                    {user?.email || 'Sign in to sync your resume'}
                  </p>
                  <span className={`plan-badge ${tier === 'pro' || tier === 'premium' ? 'premium' : 'free'}`}>
                    <FaCrown className="badge-icon" /> {tier ? tier.toUpperCase() : 'FREE PLAN'}
                  </span>
                </div>
              </div>

              {/* Credits Section */}
              <div className="settings-credits-container">
                <div className="credit-item">
                  <span className="credit-label">AI Generations</span>
                  <span className="credit-count">
                    {credits?.remainingCredits !== undefined ? credits.remainingCredits : 0}
                  </span>
                </div>
                <div className="credit-item">
                  <span className="credit-label">PDF Downloads</span>
                  <span className="credit-count">
                    {credits?.remainingCredits !== undefined ? Math.floor(credits.remainingCredits / 100) : 0}
                  </span>
                </div>
              </div>

              {/* Settings Groups */}
              <div className="settings-group">
                <h3 className="group-title">Preferences</h3>
                
                <div className="settings-row">
                  <div className="row-left">
                    <span>Dark Mode</span>
                  </div>
                  <label className="switch">
                    <input
                      id="darkModeToggle"
                      type="checkbox"
                      checked={darkMode}
                      onChange={toggleDarkMode}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                <div className="settings-row">
                  <div className="row-left">
                    <span>Resume Template</span>
                  </div>
                  <select
                    id="templateSelect"
                    className="settings-select"
                    onChange={(e) => changeTemplate(e.target.value)}
                  >
                    <option value="standard">Standard</option>
                    <option value="modern">Modern</option>
                    <option value="creative">Creative</option>
                  </select>
                </div>
              </div>

              <div className="settings-group">
                <h3 className="group-title">Account & Help</h3>
                
                {isGated ? (
                  <button className="settings-row-btn" onClick={() => navigate('/pricing')}>
                    <div className="row-left">
                      <FaCreditCard className="row-icon" />
                      <span>Upgrade & Subscription</span>
                    </div>
                    <FaChevronRight className="row-chevron" />
                  </button>
                ) : (
                  <button className="settings-row-btn" onClick={() => navigate('/settings/billing')}>
                    <div className="row-left">
                      <FaCreditCard className="row-icon" />
                      <span>Manage Subscription</span>
                    </div>
                    <FaChevronRight className="row-chevron" />
                  </button>
                )}

                {user?.email === 'andrew.green.contact@gmail.com' && (
                  <button className="settings-row-btn settings-admin-btn" onClick={() => navigate('/admin')}>
                    <div className="row-left">
                      <FaShieldAlt className="row-icon" />
                      <span>Admin Control Board</span>
                    </div>
                    <FaChevronRight className="row-chevron" />
                  </button>
                )}

                <button className="settings-row-btn" onClick={() => navigate('/about')}>
                  <div className="row-left">
                    <FaInfoCircle className="row-icon" />
                    <span>About Us</span>
                  </div>
                  <FaChevronRight className="row-chevron" />
                </button>

                <button className="settings-row-btn" onClick={() => navigate('/blog')}>
                  <div className="row-left">
                    <FaBook className="row-icon" />
                    <span>Career Blog</span>
                  </div>
                  <FaChevronRight className="row-chevron" />
                </button>

                <button className="settings-row-btn" onClick={() => navigate('/faq')}>
                  <div className="row-left">
                    <FaQuestionCircle className="row-icon" />
                    <span>Help & FAQ</span>
                  </div>
                  <FaChevronRight className="row-chevron" />
                </button>

                <button className="settings-row-btn" onClick={() => navigate('/privacy')}>
                  <div className="row-left">
                    <FaShieldAlt className="row-icon" />
                    <span>Privacy & Security</span>
                  </div>
                  <FaChevronRight className="row-chevron" />
                </button>

                <button className="settings-row-btn" onClick={() => navigate('/terms')}>
                  <div className="row-left">
                    <FaFileContract className="row-icon" />
                    <span>Terms of Service</span>
                  </div>
                  <FaChevronRight className="row-chevron" />
                </button>
              </div>

              <div className="settings-action-container">
                {user ? (
                  <button className="settings-logout-btn" onClick={() => signOut()}>
                    <FaSignOutAlt className="btn-icon" /> Log Out
                  </button>
                ) : (
                  <button className="settings-login-btn" onClick={() => navigate('/sign-in')}>
                    Sign In / Register
                  </button>
                )}
              </div>
            </div>
          )}

          {activeTab === 'preview' && (
            <div className="mobile-preview-tab">
              <div className="mobile-content-container">
                <div
                  ref={zoomContainerRef}
                  className={`resume-zoom-container ${isZoomed ? 'zoomed' : ''}`}
                  onClick={handleTapZoom}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <div ref={resumeRef} className="resume-content" style={{ transition: 'transform 0.15s ease-out' }}>
                    <MobileResume {...formData} />
                  </div>
                </div>
              </div>

              <div className="mobile-action-tray">
                <button onClick={handleWholeResumeGenerate} className="tray-btn generate-btn-full" disabled={isLoading || loadingButtons.wholeResume} aria-label="Generate Resume">
                  {loadingButtons.wholeResume ? <Spinner /> : <>⚡ Generate</>}
                </button>
                <button onClick={generatePDF} className="tray-btn generate-btn-pdf" disabled={isLoading || loadingButtons.generatePDF} aria-label="Get PDF">
                  {loadingButtons.generatePDF ? <Spinner /> : <>📥 PDF</>}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".pdf,.txt"
                  style={{ display: 'none' }}
                />
                <button onClick={handleImportClick} className="tray-btn upload-btn" disabled={isLoading || isImporting} aria-label="Upload Resume">
                  {isImporting ? <Spinner /> : <FaCloudUploadAlt />}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'editor' && (
            <MobileEditPanel
              {...formData}
              formData={formData}
              onInputChange={handleInputChange}
              onGenerateClick={handleSectionGenerate}
              loadingButtons={loadingButtons}
              characterLimits={characterLimits}
              activeSection={activeSection}
              toggleSection={toggleSection}
              isEditPanelOpen={true}
              onClose={() => handleTabChange('preview')}
              isSqueezed={isSqueezed}
              darkMode={darkMode}
              user={user}
              tier={tier}
              credits={credits}
              onShowCreditGate={handleShowCreditGate}
              handleCoverLetterGenerate={handleCoverLetterGenerate}
              isLoading={isLoading}
              fetchSubscription={fetchSubscription}
            />
          )}

          {activeTab === 'coach' && (
            <MobileChatbotPopup
              isOpen={true}
              onClose={() => handleTabChange('preview')}
              formData={formData}
              onDataUpdate={(updatedData) => setFormData((prevData) => ({ ...prevData, ...updatedData }))}
              onProposeUpdate={({ sectionKey, sectionName, proposedContent }) => {
                setProposedUpdate({
                  sectionKey,
                  sectionName,
                  currentContent: formData[sectionKey] || '',
                  proposedContent
                });
              }}
              triggerErrorPopup={(msg) => setStatusMessage({ text: msg, duration: 3000, type: 'bottom', status: 'error' })}
              onEditClick={() => handleTabChange('editor')}
              user={user}
              navigate={navigate}
              credits={credits}
              isGated={isGated}
              onShowCreditGate={handleShowCreditGate}
            />
          )}

          {/* Bottom Tab Bar Navigation Menu */}
          <div className="mobile-tab-navigation">
            <button className={`tab-item ${activeTab === 'preview' ? 'active' : ''}`} onClick={() => handleTabChange('preview')}>
              <FaEye />
              <span>Preview</span>
            </button>
            <button className={`tab-item ${activeTab === 'editor' ? 'active' : ''}`} onClick={() => handleTabChange('editor')}>
              <FaEdit />
              <span>Editor</span>
            </button>
            <button className={`tab-item ${activeTab === 'coach' ? 'active' : ''}`} onClick={() => handleTabChange('coach')}>
              <FaRobot />
              <span>AI Coach</span>
            </button>
            <button className={`tab-item ${activeTab === 'interview' ? 'active' : ''}`} onClick={() => handleTabChange('interview')}>
              <FaVideo />
              <span>Interview</span>
            </button>
            <button className={`tab-item ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => handleTabChange('settings')}>
              <FaCog />
              <span>Settings</span>
            </button>
          </div>

          <CreditGateModal
            isOpen={isCreditModalOpen}
            onClose={() => setIsCreditModalOpen(false)}
            onConfirm={onCreditModalConfirm}
            type={creditModalType}
            balance={credits?.remainingCredits || 0}
          />

          {isGenerating && (
            <div className="generation-modal-overlay">
              <div className="generation-modal-card">
                <div className="ai-pulse-sphere">
                  <img src="/logo.png" alt="Career Vision AI Logo" className="generation-logo" />
                  <div className="wave wave1"></div>
                  <div className="wave wave2"></div>
                  <div className="wave wave3"></div>
                </div>
                <h3 className="generation-title">AI Engine Active</h3>
                <div className="generation-progress-container">
                  <div className="generation-progress-fill"></div>
                </div>
                <div className="generation-status-text">
                  {generationProgressMessage || 'Processing resume data...'}
                </div>
                <div className="generation-tips">
                  <span className="tip-bullet">⚡</span>
                  <span className="tip-text">AI is structuring, writing, and proofreading your profile sections automatically.</span>
                </div>
              </div>
            </div>
          )}

          {proposedUpdate && (
            <ResumeUpdateModal
              sectionName={proposedUpdate.sectionName}
              currentContent={proposedUpdate.currentContent}
              proposedContent={proposedUpdate.proposedContent}
              onApprove={() => {
                setFormData((prevData) => ({
                  ...prevData,
                  [proposedUpdate.sectionKey]: proposedUpdate.proposedContent,
                }));
                setProposedUpdate(null);
                setStatusMessage({ text: `${proposedUpdate.sectionName} updated successfully!`, duration: 3000, status: 'success' });
              }}
              onReject={() => {
                setProposedUpdate(null);
              }}
              isMobile={true}
            />
          )}
      </div>
    </div>
  );
};


export default MobileApp;